<template>
  <div
    class="primary-lighten-1 mb-2 space-y-2 rounded-md p-2"
    :class="{ 'pointer-events-none opacity-50': isDeleting }"
  >
    <div class="flex flex-grow gap-x-2">
      <PFormField label="Machine" sr-only class="flex-grow">
        <InputCombobox
          v-model="modelValue.machine"
          :items="selectableMachines"
          items-key-name="id"
          items-value-name="name"
          creation-option
          :disable-control="$attrs.disabled as boolean"
          :creation-model-type="Machine"
          auto-select-newly-created-model
          @item-selected="onItemSelected"
          @focusout="onFocusOut"
          ref="machineComboboxRef"
        />
      </PFormField>
      <PButton
        :icon="['fas', 'trash-can']"
        variant="ghost"
        class="hover:text-error py-0!"
        @click="deleteRoleServiceProviderMachine"
      />
      <PButton
        :icon="['fas', 'grip-vertical']"
        variant="ghost"
        class="drag-handle cursor-grab! py-0!"
      />
    </div>
    <PFormField label="Standaardin- en verkoopartikel" sr-only>
      <InputCombobox
        v-model="selectedArticleOption"
        :items="articleOptions"
        :items-sort-fn="sortArticleOptions"
        @query-changed="articleQuery = $event"
        items-key-name="key"
        :items-value-function="(item) => item.getSearchText()"
        :edit-model-conversion-function="(item) => item.purchaseItem ?? item.saleItem"
        :custom-edit-obj-path="selectedArticleOption?.purchaseItem ? 'purchaseItem' : 'saleItem'"
        search-thoroughly
        :disabled="!modelValue.machine"
        input-placeholder="Standaardin- en verkoopartikel"
        :creation-model-type="PurchaseItem"
        :auto-select-newly-created-model="false"
        @item-created="onArticleSaved"
        @item-updated="onArticleSaved"
        @item-selected="updateRoleServiceProviderMachine"
        @item-deselected="updateRoleServiceProviderMachine"
        ref="purchaseItemComboboxRef"
      >
        <template #custom-list-item="{ item, active }">
          <div class="block py-2 pl-2" :class="{ 'primary cursor-pointer': active }">
            <ComboboxPurchaseItemDisplay v-if="item.purchaseItem" :item="item.purchaseItem" />
            <ComboboxSaleItemDisplay v-if="item.saleItem" :item="item.saleItem" />
          </div>
        </template>
        <template #custom-selected-item="{ item }">
          <ComboboxPurchaseItemDisplay v-if="item.purchaseItem" :item="item.purchaseItem" />
          <ComboboxSaleItemDisplay v-if="item.saleItem" :item="item.saleItem" />
        </template>
      </InputCombobox>
      <p v-if="hasInvalidPair" class="text-error mt-1 text-xs">
        Deze in- en verkoopartikelen zijn niet meer gekoppeld. Kies opnieuw.
      </p>
    </PFormField>
  </div>
</template>

<script setup lang="ts">
import PFormField from "@/components/form/PFormField.vue"
import { Machine } from "@/models/machine.model.ts"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import PButton from "@/components/elements/PButton.vue"
import { storeToRefs } from "pinia"
import type { RoleServiceProviderMachine } from "@/models/role-service-provider-machine.model.ts"
import { computed, ref } from "vue"
import { usePurchaseItemStore } from "@/store/purchase-items.store.ts"
import ComboboxSaleItemDisplay from "@/components/administration/combobox-display-templates/ComboboxSaleItemDisplay.vue"
import { SaleItem } from "@/models/sale-item.model"
import { useSaleItemStore } from "@/store/sale-items.store"
import ComboboxPurchaseItemDisplay from "@/components/administration/combobox-display-templates/ComboboxPurchaseItemDisplay.vue"
import { PurchaseItem } from "@/models/purchase-item.model.ts"
import { AdministrativeEventTypes } from "@/enums/administrative-event-types.enum"
import { InstructionProcessingRowComboboxHelper } from "@/helper/instruction/instruction-processing-row-combobox-helper"

defineProps<{
  selectableMachines: Machine[]
}>()

const emit = defineEmits(["update:modelValue", "delete"])

const purchaseItemStore = usePurchaseItemStore()
const { purchaseItems } = storeToRefs(purchaseItemStore)
const { saleItems } = storeToRefs(useSaleItemStore())

const modelValue = defineModel<RoleServiceProviderMachine>({ required: true })

type ArticleOption = ReturnType<typeof buildArticleOption>

function buildArticleOption(purchaseItem: PurchaseItem | null, saleItem: SaleItem | null) {
  const type = purchaseItem
    ? saleItem
      ? AdministrativeEventTypes.PURCHASE_AND_SALE
      : AdministrativeEventTypes.PURCHASE
    : AdministrativeEventTypes.SALE
  return {
    key: `${type}-${purchaseItem?.id ?? ""}-${saleItem?.id ?? ""}`,
    type,
    purchaseItem,
    saleItem,
    getSearchText: () =>
      purchaseItem
        ? saleItem
          ? InstructionProcessingRowComboboxHelper.getTransactionGroupSearchText(
              purchaseItem,
              saleItem
            )
          : InstructionProcessingRowComboboxHelper.getPurchaseItemSearchText(purchaseItem)
        : InstructionProcessingRowComboboxHelper.getSaleItemSearchText(saleItem!)
  }
}

// Rank before InputCombobox applies its result limit, so sale-only options remain reachable.
const articleOptions = computed(() =>
  [
    ...purchaseItems.value.flatMap((item) => [
      buildArticleOption(item, null),
      ...(item.saleItem ? [buildArticleOption(item, item.saleItem)] : [])
    ]),
    ...saleItems.value.map((item) => buildArticleOption(null, item))
  ].sort(sortArticleOptions)
)

const articleQuery = ref("")

function sortArticleOptions(a: ArticleOption, b: ArticleOption) {
  const query = articleQuery.value.toLowerCase()
  // Match the processing combobox: pairs first, with explicit type search taking priority.
  const preferredType = query.includes("inkoop")
    ? AdministrativeEventTypes.PURCHASE
    : query.includes("verkoop")
      ? AdministrativeEventTypes.SALE
      : null
  if (preferredType !== null) {
    const aIsPreferred = a.type === preferredType
    const bIsPreferred = b.type === preferredType
    if (aIsPreferred !== bIsPreferred) return aIsPreferred ? -1 : 1
  }

  if (a.type === b.type) {
    const aIncludes = a.getSearchText().toLowerCase().includes(query)
    const bIncludes = b.getSearchText().toLowerCase().includes(query)
    if (aIncludes !== bIncludes) return aIncludes ? -1 : 1
  }

  return a.type - b.type
}

const hasInvalidPair = computed(() => {
  const { purchaseItem, saleItem } = modelValue.value
  return !!(purchaseItem && saleItem && purchaseItem.saleItem?.id !== saleItem.id)
})

const selectedArticleOption = computed<ArticleOption | null>({
  get() {
    const { purchaseItem, saleItem } = modelValue.value
    if (hasInvalidPair.value) return null
    return purchaseItem || saleItem ? buildArticleOption(purchaseItem, saleItem) : null
  },
  set(option) {
    modelValue.value.purchaseItem = option?.purchaseItem ?? null
    modelValue.value.saleItem = option?.saleItem ?? null
  }
})

const lastSelectedMachine = ref<Machine | undefined>()

function onItemSelected() {
  lastSelectedMachine.value = modelValue.value.machine
  updateRoleServiceProviderMachine()
}

function onFocusOut() {
  if (!modelValue.value.machine && lastSelectedMachine.value) {
    modelValue.value.machine = lastSelectedMachine.value
  }
}

function onArticleSaved(item: PurchaseItem | SaleItem) {
  if (item instanceof PurchaseItem) {
    modelValue.value.purchaseItem = item
  } else {
    modelValue.value.saleItem = item
  }
  if (!hasInvalidPair.value) updateRoleServiceProviderMachine()
}

function updateRoleServiceProviderMachine() {
  emit("update:modelValue", modelValue.value)
}

const isDeleting = ref(false)
function deleteRoleServiceProviderMachine() {
  isDeleting.value = true
  emit("delete", modelValue.value)
}

const machineComboboxRef = ref<typeof InputCombobox>()
function focusMachineCombobox() {
  machineComboboxRef.value!.focus()
}

const purchaseItemComboboxRef = ref<typeof InputCombobox>()
function focusPurchaseItemCombobox() {
  purchaseItemComboboxRef.value!.focus()
}

defineExpose({ focusMachineCombobox, focusPurchaseItemCombobox })
</script>

<style scoped></style>
